import { useEffect } from "react";
import { initialSsrTitle } from "@/lib/initial-ssr-meta";
import { useSeoMeta } from "@/hooks/use-cms";
import type { SeoMeta } from "@shared/schema";

interface MetaTagsProps {
  pageUrl: string;
  fallback?: {
    title?: string;
    description?: string;
    keywords?: string[];
  };
}

export function MetaTags({ pageUrl, fallback = {} }: MetaTagsProps) {
  const { data: seoData } = useSeoMeta(pageUrl);

  useEffect(() => {
    if (!seoData && !fallback.title) return;

    // Update title
    const title = seoData?.title || initialSsrTitle(pageUrl) || fallback.title;
    if (title) {
      document.title = title;
    }

    // Update meta description
    const description = seoData?.description || fallback.description;
    if (description) {
      let descriptionMeta = document.querySelector('meta[name="description"]');
      if (!descriptionMeta) {
        descriptionMeta = document.createElement('meta');
        descriptionMeta.setAttribute('name', 'description');
        document.head.appendChild(descriptionMeta);
      }
      descriptionMeta.setAttribute('content', description);
    }

    // Update keywords
    const keywords = seoData?.keywords || fallback.keywords;
    if (keywords && keywords.length > 0) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (!keywordsMeta) {
        keywordsMeta = document.createElement('meta');
        keywordsMeta.setAttribute('name', 'keywords');
        document.head.appendChild(keywordsMeta);
      }
      const keywordsString = Array.isArray(keywords) ? keywords.join(', ') : keywords;
      keywordsMeta.setAttribute('content', keywordsString);
    }

    // Update Open Graph tags
    if (seoData?.ogTitle || title) {
      let ogTitleMeta = document.querySelector('meta[property="og:title"]');
      if (!ogTitleMeta) {
        ogTitleMeta = document.createElement('meta');
        ogTitleMeta.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitleMeta);
      }
      ogTitleMeta.setAttribute('content', seoData?.ogTitle || title || '');
    }

    if (seoData?.ogDescription || description) {
      let ogDescriptionMeta = document.querySelector('meta[property="og:description"]');
      if (!ogDescriptionMeta) {
        ogDescriptionMeta = document.createElement('meta');
        ogDescriptionMeta.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescriptionMeta);
      }
      ogDescriptionMeta.setAttribute('content', seoData?.ogDescription || description || '');
    }

    if (seoData?.ogImage) {
      let ogImageMeta = document.querySelector('meta[property="og:image"]');
      if (!ogImageMeta) {
        ogImageMeta = document.createElement('meta');
        ogImageMeta.setAttribute('property', 'og:image');
        document.head.appendChild(ogImageMeta);
      }
      ogImageMeta.setAttribute('content', seoData.ogImage);
    }

    // Update canonical URL
    if (seoData?.canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', seoData.canonicalUrl);
    }

    // Update robots directive
    if (seoData?.robots) {
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', seoData.robots);
    }

    // Add structured data if available
    if (seoData?.structuredData) {
      let scriptTag = document.querySelector('script#structured-data[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('id', 'structured-data');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(seoData.structuredData);
    }
  }, [seoData, fallback, pageUrl]);

  return null; // This component doesn't render anything visible
}